import { COLORS } from "../core/constants.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { AVATAR_PALETTE } from "../core/palette.ts";
import { drawLitPanel } from "./UIMaterial.ts";
import { getSharedUIBloom } from "./ProBar.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import {
  UI_SPACE,
  UI_EASE,
  UI_DURATION,
  UI_FONT_STACK,
  roleFontSize,
} from "./uiTokens.ts";

// S144/BL-135: the mascots are now **AI character art** (gpt-image-1, owner-
// directed), shipped as lean transparent webp in `public/img/characters/` and
// loaded as `<img src>` (NOT inline `?raw` SVG). One locked face per character
// drives a consistent expression set (see tools/character-pipeline + the proof
// sheet). The baked game palette still lives in the FRAME (--mascot-status); the
// raster never recolors. This replaced the S44 inline-SVG sticker mascots; the
// figure-inner CSS hooks they carried (lens-glint / wink-lid / glasses-detach)
// are gone with raster — the per-pose art now carries the mood, and the WOW
// flourish keeps its frame gold-surge + glitter (the glasses-fly was SVG-only).
const CHAR_ART_BASE = "/img/characters/";

// "wow" is the exclusive kernel_panic-deploy reaction (gold frame, glitter). It
// is event-only — fired via the comms arbiter, never an ambient mood.
export type VeraPose =
  | "idle"
  | "alert"
  | "alarm"
  | "cheer"
  | "down"
  | "wow"
  | "thinking"
  | "worried"
  | "proud";
export type MascotCharacter = "vera" | "fenrir";
// FENRIR expressions: idle (smug/neutral), cheer (gloat/victory), angry (snarl),
// plus the S144 range: smug (cocky), taunting (mocking), defeated (purged).
export type FenrirExpr =
  | "idle"
  | "cheer"
  | "angry"
  | "smug"
  | "taunting"
  | "defeated";
// Start/end-screen hero art (full head + headroom; taller renders). Owner-added
// S144 scope, generated through the same pipeline + locked reference.
export type MascotHero = "start" | "success" | "fail";

/** Human-readable alt text per state (a11y; the mascot containers are decorative/
 *  aria-hidden, so this is belt-and-suspenders, never a translated UI label). */
function mascotAlt(character: MascotCharacter, state: string): string {
  return character === "fenrir"
    ? `FENRIR, the wolf-herald — ${state}`
    : `VERA, the operator — ${state}`;
}

export interface MascotOptions {
  character: MascotCharacter; // "vera" (9 poses) | "fenrir" (6 expressions)
  pose?: VeraPose; // VERA only; default "idle"
  fenrirExpr?: FenrirExpr; // FENRIR only; default "idle"
  /** Render a start/end-screen HERO portrait (full head + headroom) instead of
   *  the in-game expression art. Decorative; pose/expr are ignored when set. */
  heroArt?: MascotHero;
  size?: number; // px height; width auto from viewBox
  frame?: boolean; // wrap in the chrome panel (border/glow/scanlines)
  label?: string; // nameplate text — identity only, e.g. "OPERATOR: AAA"
  quip?: string; // static speech-bubble line (e.g. GameOver taunt)
  className?: string;
  /** BL-48: upgrade this framed panel to a **lit comms console** — the real
   *  S86 engine material behind the figure (a `drawLitPanel` canvas, bloomed via
   *  the shared `UIBloom`) + corner brackets + a settle/value-flash on pose/quip
   *  change. Opt-in (the live HUD VERA×FENRIR portrait only) so the menu/dossier
   *  framed mascots keep the plain frame. Requires `frame: true`. */
  litConsole?: boolean;
}

/** Payload gold — matches the kernel_panic detonation/card glow (DraftUI, PayloadDetonationFx).
    Sourced from the central avatar palette (core/palette.ts). */
const KERNEL_PANIC_GOLD = AVATAR_PALETTE.vera.wow;

/** Pose → frame/accent status color (green idle/cheer, amber alert, red alarm/down).
    Status is conveyed by COLOR only now — never as nameplate text (the nameplate is
    pure identity, e.g. "OPERATOR: AAA"); event mood is surfaced via the quip bubble.
    Exported so the BL-61 commentary TV re-tints its set from the SAME central source
    (no second pose→colour table). */
export const POSE_COLOR: Record<VeraPose, string> = {
  idle: AVATAR_PALETTE.vera.idle,
  cheer: AVATAR_PALETTE.vera.cheer,
  alert: AVATAR_PALETTE.vera.alert,
  alarm: AVATAR_PALETTE.vera.alarm,
  down: AVATAR_PALETTE.vera.down,
  wow: AVATAR_PALETTE.vera.wow,
  thinking: AVATAR_PALETTE.vera.thinking,
  worried: AVATAR_PALETTE.vera.worried,
  proud: AVATAR_PALETTE.vera.proud,
};

/** Build the `<img>` markup for a character art asset. One central seam (BL-61)
 *  so the commentary TV renders the EXACT same owner-approved assets the portrait
 *  loads — one art source, no duplicate paths / redraw. */
function imgMarkup(file: string, alt: string): string {
  return `<img class="mascot-img" src="${CHAR_ART_BASE}${file}.webp" alt="${alt}" draggable="false" />`;
}

/** Resolve the raster markup for a figure state (S144: AI character art). */
export function getMascotMarkup(
  character: MascotCharacter,
  pose: VeraPose,
  fenrirExpr: FenrirExpr = "idle",
): string {
  const state = character === "fenrir" ? fenrirExpr : pose;
  return imgMarkup(`${character}-${state}`, mascotAlt(character, state));
}

/** Resolve the raster markup for a start/end-screen HERO (full head + headroom). */
export function getMascotHeroMarkup(
  character: MascotCharacter,
  hero: MascotHero,
): string {
  return imgMarkup(`${character}-${hero}`, mascotAlt(character, hero));
}

/** The in-game figure states the commentary TV can put on air. S193 (BL-275):
 *  preloaded + decoded once at TV construction so a mid-combat channel switch
 *  swaps cached bitmaps instead of fetching/decoding on the hot path. */
const IN_GAME_FIGURES: readonly string[] = [
  "vera-idle",
  "vera-alert",
  "vera-alarm",
  "vera-cheer",
  "vera-down",
  "vera-wow",
  "vera-thinking",
  "vera-worried",
  "vera-proud",
  "fenrir-idle",
  "fenrir-cheer",
  "fenrir-angry",
  "fenrir-smug",
  "fenrir-taunting",
  "fenrir-defeated",
];

let mascotArtPreloaded = false;

/** Warm the browser cache + decoder for every in-game figure (idempotent,
 *  off the hot path; ~15 lean webp). Headless/jsdom → silent no-op. */
export function preloadMascotArt(): void {
  if (mascotArtPreloaded || typeof Image === "undefined") return;
  mascotArtPreloaded = true;
  for (const file of IN_GAME_FIGURES) {
    const img = new Image();
    img.src = `${CHAR_ART_BASE}${file}.webp`;
    // decode() warms the bitmap decoder where supported; failures (headless,
    // cancelled navigation) are irrelevant — the cache warm-up already helped.
    img.decode?.().catch(() => {});
  }
}

function setNameplate(el: HTMLElement, text: string): void {
  const plate = el.querySelector(".mascot-nameplate");
  if (plate) plate.textContent = text;
}

/** Show a transient/static speech-bubble line above the mascot. */
export function setMascotQuip(el: HTMLElement, text: string): void {
  const bubble = el.querySelector(".mascot-quip");
  if (!bubble) return;
  bubble.textContent = text;
  bubble.classList.add("quip-show");
  // A new transmission gives the console a brief value-flash/settle (console only).
  refreshConsole(el);
}

/** Hide the quip bubble. */
export function clearMascotQuip(el: HTMLElement): void {
  const bubble = el.querySelector(".mascot-quip");
  if (bubble) bubble.classList.remove("quip-show");
}

/**
 * One-shot gold glitter burst over the portrait (kernel_panic deploy). Pure
 * render-only DOM confetti: each sparkle is a self-removing <span> driven by a
 * single CSS keyframe — no rAF loop, no canvas, no sim coupling. Honors
 * prefers-reduced-motion (no-op). Math.random is render-only → determinism-safe.
 */
export function burstMascotGlitter(el: HTMLElement, count = 16): void {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  ) {
    return;
  }
  for (let i = 0; i < count; i++) {
    const s = document.createElement("span");
    s.className = "mascot-glitter";
    const angle = Math.random() * Math.PI * 2;
    const dist = 24 + Math.random() * 42;
    s.style.setProperty("--gx", `${(Math.cos(angle) * dist).toFixed(1)}px`);
    // Bias upward so the spray reads as "blasting off" with the glasses.
    s.style.setProperty("--gy", `${(Math.sin(angle) * dist - 16).toFixed(1)}px`);
    s.style.setProperty("--gd", `${(Math.random() * 0.28).toFixed(2)}s`);
    s.style.setProperty("--gsz", `${(5 + Math.random() * 6).toFixed(1)}px`);
    s.style.left = `${(38 + Math.random() * 24).toFixed(1)}%`;
    s.style.top = `${(30 + Math.random() * 24).toFixed(1)}%`;
    s.addEventListener("animationend", () => s.remove(), { once: true });
    el.appendChild(s);
  }
}

// ─── Lit comms console (BL-48) — the real S86 material behind the framed VERA ──
//
// Only the live HUD portrait opts in (`litConsole: true`). A `drawLitPanel`
// canvas paints the lit-CRT frame (the arena's own `emissiveFill`/`glowCache`)
// behind the figure, bloomed through the ONE shared `UIBloom` — dirty-redrawn on
// mount/resize/pose change, NOT per-frame (the panel is static, like the HUD
// chrome). The redraw closure is kept per-element so the pose-change handlers can
// recolour the frame to the new status. Null-ctx / headless → silent no-op.

/** How far the lit frame canvas overscans the panel so the bloom can bleed. */
const CONSOLE_OVERSCAN = 10;

/** element → "repaint the lit frame at the current --mascot-status" closure. */
const consoleRedraw = new WeakMap<HTMLElement, () => void>();

function paintLitConsole(canvas: HTMLCanvasElement, host: HTMLElement): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const cw = host.clientWidth;
  const ch = host.clientHeight;
  if (cw <= 0 || ch <= 0) return;
  const dpr = Math.max(
    1,
    displayDpr(),
  );
  const O = CONSOLE_OVERSCAN;
  const W = cw + O * 2;
  const H = ch + O * 2;
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);

  const colour =
    host.style.getPropertyValue("--mascot-status").trim() ||
    COLORS.SENTINEL_GREEN;
  // The real engine material — a lit CRT frame, brighter than a default panel.
  drawLitPanel(ctx, {
    rect: { x: O, y: O, w: cw, h: ch },
    colour,
    state: "active",
    intensity: 1.15,
    cornerRadius: 8,
    edgeRadius: 16,
  });

  // Route the lit frame through the ONE shared UI bloom (the arena's GLSL).
  const bloom = getSharedUIBloom();
  if (bloom) {
    bloom.setDpr(dpr);
    const out = bloom.process(canvas);
    if (out) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 0.9;
      ctx.drawImage(out, 0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
    }
  }
}

/** Mount the lit-frame canvas + observe the host so it repaints on resize. */
function mountLitConsole(root: HTMLElement): void {
  root.classList.add("mascot-console");
  const canvas = document.createElement("canvas");
  canvas.className = "mascot-litframe";
  canvas.setAttribute("aria-hidden", "true");
  root.insertBefore(canvas, root.firstChild);
  const redraw = (): void => paintLitConsole(canvas, root);
  consoleRedraw.set(root, redraw);
  if (typeof ResizeObserver !== "undefined") {
    const obs = new ResizeObserver(() => redraw());
    obs.observe(root);
  }
  // Defer the first paint until layout settles (clientWidth is 0 pre-layout).
  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(redraw);
  } else {
    redraw();
  }
}

/** Recolour the lit frame + play a brief settle/value-flash on a status change.
 *  No-op for non-console mascots (the menu/dossier framed figures). */
function refreshConsole(el: HTMLElement): void {
  const redraw = consoleRedraw.get(el);
  if (!redraw) return;
  redraw();
  if (isReducedMotion()) return;
  el.classList.remove("mascot-settle");
  void el.offsetWidth; // reflow so the settle keyframe re-triggers
  el.classList.add("mascot-settle");
}

/** Decorative, non-interactive element: framed (optional) posed figure. */
export function createMascot(opts: MascotOptions): HTMLElement {
  injectStyles();
  const {
    character,
    pose = "idle",
    fenrirExpr = "idle",
    size = 200,
    frame = false,
    label,
    quip,
    className,
    litConsole = false,
    heroArt,
  } = opts;

  const root = document.createElement("div");
  root.className = `operator-mascot${frame ? " mascot-framed" : ""}${
    className ? ` ${className}` : ""
  }`;
  root.setAttribute("aria-hidden", "true");
  root.dataset.character = character;
  root.dataset.pose = character === "fenrir" ? "idle" : pose;
  if (character === "fenrir") root.dataset.fenrirExpr = fenrirExpr;
  root.style.setProperty("--mascot-size", `${size}px`);
  root.style.setProperty(
    "--mascot-status",
    character === "fenrir" ? AVATAR_PALETTE.fenrir.frame : POSE_COLOR[pose],
  );
  // Faction badge — the figure's backing emblem resolves from the central
  // palette (VERA = mainframe cool, FENRIR = enemy threat). See the SVG fills.
  if (character === "fenrir") {
    root.style.setProperty("--avatar-badge", AVATAR_PALETTE.fenrir.badge);
  } else {
    root.style.setProperty("--avatar-badge", AVATAR_PALETTE.vera.badge);
    root.style.setProperty("--avatar-badge-back", AVATAR_PALETTE.vera.badgeBack);
  }
  // Speech-bubble quip (event reactions / taunts). Always present; hidden until
  // a quip is set. Sits above the figure (frame uses overflow:visible so it shows).
  const bubble = document.createElement("div");
  bubble.className = "mascot-quip";
  if (quip) {
    bubble.textContent = quip;
    bubble.classList.add("quip-show");
  }
  root.appendChild(bubble);

  const figure = document.createElement("div");
  figure.className = "mascot-figure";
  figure.innerHTML = heroArt
    ? getMascotHeroMarkup(character, heroArt)
    : getMascotMarkup(character, pose, fenrirExpr);
  if (heroArt) root.classList.add("mascot-hero");
  root.appendChild(figure);

  if (label) {
    const plate = document.createElement("div");
    plate.className = "mascot-nameplate";
    plate.textContent = label;
    // Identity label (no status suffix). Stored so a reactive portrait can
    // restore the operator name after a FENRIR boss-swap.
    root.dataset.labelBase = label;
    root.appendChild(plate);
  }

  // BL-48: opt-in lit comms console — the real engine material behind VERA.
  if (frame && litConsole) mountLitConsole(root);
  return root;
}

/** Swap pose on an existing VERA mascot (the reactive HUD portrait). No-op during a FENRIR boss-swap. */
export function setMascotPose(el: HTMLElement, pose: VeraPose): void {
  if (el.dataset.character !== "vera") return;
  const figure = el.querySelector(".mascot-figure");
  if (figure) figure.innerHTML = getMascotMarkup("vera", pose);
  el.dataset.pose = pose;
  el.style.setProperty("--mascot-status", POSE_COLOR[pose]);
  refreshConsole(el);
}

/** Swap the whole figure between VERA and FENRIR in place (HUD boss-wave surface).
    `fenrirExpr` picks the boss expression (idle smug / cheer gloat / angry snarl). */
export function setMascotCharacter(
  el: HTMLElement,
  character: MascotCharacter,
  fenrirExpr: FenrirExpr = "idle",
): void {
  const figure = el.querySelector(".mascot-figure");
  if (figure) figure.innerHTML = getMascotMarkup(character, "idle", fenrirExpr);
  el.dataset.character = character;
  el.dataset.pose = "idle";
  if (character === "fenrir") {
    el.dataset.fenrirExpr = fenrirExpr;
    el.style.setProperty("--mascot-status", AVATAR_PALETTE.fenrir.frame);
    el.style.setProperty("--avatar-badge", AVATAR_PALETTE.fenrir.badge);
    el.style.removeProperty("--avatar-badge-back");
    setNameplate(el, "ORACLE: FENRIR");
  } else {
    delete el.dataset.fenrirExpr;
    el.style.setProperty("--mascot-status", POSE_COLOR.idle);
    el.style.setProperty("--avatar-badge", AVATAR_PALETTE.vera.badge);
    el.style.setProperty("--avatar-badge-back", AVATAR_PALETTE.vera.badgeBack);
    if (el.dataset.labelBase !== undefined)
      setNameplate(el, el.dataset.labelBase);
  }
  refreshConsole(el);
}

// ─── BL-61 · the render-surface seam ──────────────────────────────────────────
//
// The comms arbiter (`BeatScheduler`) used to push DOM straight onto the one
// portrait `el` (`setMascotCharacter(el,…)` / `setMascotPose` / `setMascotQuip`).
// That hard-wired the arbiter to ONE surface. `MascotSurface` is the thin seam:
// the arbiter now drives an interface, and there can be many implementations of
// it — the framed DOM portrait below, AND the BL-61 commentary TV — without
// duplicating the ~24 event subscriptions. The arbiter decides WHAT the operator
// channel says; the surface decides HOW it is shown.

/** What the comms arbiter needs of a render surface. The same five state pushes
 *  the portrait always took, plus the two transient flourishes (FENRIR glitch
 *  break-in, the kernel_panic gold glitter). Render-only — no sim/RNG coupling. */
export interface MascotSurface {
  /** Switch the active caller (and, for FENRIR, the snarl/gloat expression). */
  setCharacter(character: MascotCharacter, fenrirExpr?: FenrirExpr): void;
  /** Set VERA's pose (no-op while FENRIR holds the surface). */
  setPose(pose: VeraPose): void;
  /** Show the current comms line. */
  setQuip(line: string): void;
  /** Hide the line. */
  clearQuip(): void;
  /** Trigger the transient FENRIR break-in interference cut. */
  pulseGlitch(): void;
  /** Clear the interference cut (revert / game-over). */
  clearGlitch(): void;
  /** Spray the kernel_panic gold-payload glitter over the surface. */
  burstGlitter(): void;
  /** S130 (Story Bible §6.3): ECHELON's SIGNAL OVERRIDE — the antagonist takes
   *  the surface (she never gets a desk or a channel of her own). Optional: a
   *  surface without it falls back to a glitch + plain quip in the scheduler. */
  playHijack?(line: string): void;
  /** End the override; the surface restores the rightful caller's chrome. */
  clearHijack?(): void;
  /** Task-013 (AC-17/O-1+O-8) — a priority beat (boss spawn / new-signature
   *  enemy debut / breach) just landed: the surface may pop out large over the
   *  board with a shake for the read duration, then dock back. Optional: a
   *  surface without it (e.g. the legacy framed portrait) simply has no pop-out
   *  FX — the arbiter's beat detection is unchanged either way. */
  popOutBeat?(beat: TvPopOutBeat): void;
}

/** Task-013 — the pop-out FX beats, named after the real event each keys off
 *  (`boss.spawned`, the new-signature branch of `enemy.spawned`,
 *  `breach.completed`). No new EventBus events — this is purely which of the
 *  arbiter's already-detected beats the surface should react to visually. */
export type TvPopOutBeat = "boss" | "newSignature" | "breach";

/**
 * Wrap an existing framed `OperatorMascot` DOM element as a `MascotSurface`.
 * This is a 1:1 adapter over the free functions + the `hud-mascot-glitch` class
 * the arbiter used directly before BL-61, so the DOM portrait's behaviour is
 * byte-for-byte unchanged — it stays a valid surface for any consumer that still
 * wants the framed portrait (and keeps the existing arbiter tests green).
 */
export function createDomMascotSurface(el: HTMLElement): MascotSurface {
  return {
    setCharacter: (character, fenrirExpr) =>
      setMascotCharacter(el, character, fenrirExpr),
    setPose: (pose) => setMascotPose(el, pose),
    setQuip: (line) => setMascotQuip(el, line),
    clearQuip: () => clearMascotQuip(el),
    pulseGlitch: () => {
      el.classList.remove("hud-mascot-glitch");
      void el.offsetWidth; // reflow so the glitch keyframe re-triggers
      el.classList.add("hud-mascot-glitch");
    },
    clearGlitch: () => el.classList.remove("hud-mascot-glitch"),
    burstGlitter: () => burstMascotGlitter(el),
  };
}

function injectStyles(): void {
  if (document.getElementById("operator-mascot-styles")) return;
  const style = document.createElement("style");
  style.id = "operator-mascot-styles";
  style.textContent = `
    .operator-mascot {
      position: relative;
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      pointer-events: none;
      line-height: 0;
    }
    .operator-mascot .mascot-figure { position: relative; line-height: 0; }
    /* S144: the figure is now raster character art (<img>). Unframed instances
       (boot / pre-run / end-screen flanks) show the full upper-body portrait
       sized to height. */
    .operator-mascot .mascot-figure img {
      display: block;
      height: var(--mascot-size, 200px);
      width: auto;
      -webkit-user-drag: none;
      user-select: none;
    }
    /* Canonical framed "comms portrait" crop — a fixed square box; the raster is
       object-fit:cover, biased to the top so it reads head-and-shoulders for
       every state (the art is centered head-to-waist). Identical framing across
       poses AND for FENRIR → no crop switching between states. */
    .operator-mascot.mascot-framed .mascot-figure {
      width: var(--mascot-size, 96px);
      height: var(--mascot-size, 96px);
      overflow: hidden;
    }
    .operator-mascot.mascot-framed .mascot-figure img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: 50% 8%;
    }
    .operator-mascot.mascot-framed {
      position: relative;
      /* overflow visible so the quip bubble can sit above the frame; the figure
         crop is clipped by .mascot-figure's own overflow:hidden. */
      overflow: visible;
      padding: 6px;
      background: ${COLORS.BG_PRIMARY};
      border: 1px solid var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      box-shadow: 0 0 12px var(--mascot-status, ${COLORS.SENTINEL_GREEN}),
        inset 0 0 8px rgba(0, 0, 0, 0.6);
      transition: border-color 0.3s ease, box-shadow 0.3s ease;
    }
    .operator-mascot.mascot-framed::after {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 2;
      pointer-events: none;
      background: repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.18) 2px,
        rgba(0, 0, 0, 0.18) 4px
      );
    }
    .operator-mascot .mascot-nameplate {
      position: relative;
      z-index: 3;
      margin-top: 6px;
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize("micro")};
      letter-spacing: 2px;
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;
      color: var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      text-shadow: 0 0 6px var(--mascot-status, ${COLORS.SENTINEL_GREEN});
    }

    /* ===== Event quip bubble (VERA reactions / FENRIR taunts) ===== */
    .operator-mascot .mascot-quip {
      position: absolute;
      bottom: calc(100% + 6px);
      left: 50%;
      transform: translateX(-50%) translateY(4px) scale(0.92);
      padding: 4px 9px;
      background: rgba(5, 8, 5, 0.94);
      border: 1px solid var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      border-radius: 3px;
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize("micro")};
      font-weight: bold;
      letter-spacing: 1px;
      line-height: 1.2;
      white-space: nowrap;
      color: var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      text-shadow: 0 0 6px var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.7),
        0 0 8px var(--mascot-status, ${COLORS.SENTINEL_GREEN});
      opacity: 0;
      pointer-events: none;
      z-index: 6;
      transition: opacity 0.18s ease, transform 0.18s ease;
    }
    /* little downward tail */
    .operator-mascot .mascot-quip::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: var(--mascot-status, ${COLORS.SENTINEL_GREEN});
    }
    .operator-mascot .mascot-quip.quip-show {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }

     /* ===== Framed comms-portrait: quip becomes an in-frame caption UNDER the
       nameplate (no floating bubble). The caption band reserves fixed 2-line
       space at all times, so showing/hiding text never changes the frame box. ===== */
    .operator-mascot.mascot-framed { flex-direction: column; }
    .operator-mascot.mascot-framed .mascot-figure { order: 0; }
    .operator-mascot.mascot-framed .mascot-nameplate { order: 1; }
    .operator-mascot.mascot-framed .mascot-quip {
      order: 2;
      position: static;
      left: auto;
      bottom: auto;
      transform: translateY(2px);
      max-width: 100%;
      width: 100%;
      box-sizing: border-box;
      padding: 0 2px;
      margin-top: 4px;
      background: transparent;
      border: none;
      border-radius: 0;
      box-shadow: none;
      font-size: ${roleFontSize("micro")};
      letter-spacing: 0.5px;
      white-space: normal;
      overflow-wrap: anywhere;
      text-align: center;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      height: 2.8em;
      min-height: 2.8em;
      opacity: 0;
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
    .operator-mascot.mascot-framed .mascot-quip::after { display: none; }
    .operator-mascot.mascot-framed .mascot-quip.quip-show {
      transform: translateY(0);
      opacity: 1;
    }

    /* ===== Idle micro-animation (frame-level, pure CSS, no JS/rAF loop) =====
       S193 (BL-273): the SVG-era glint-sweep / wolf-wink / glasses-fly rules
       are retired — they targeted hooks inside the old inline SVG figures,
       which cannot exist inside the S144 raster <img>. The per-state ART
       carries the mood; the frame flicker below is live and stays. */
    /* Subtle CRT flicker on the framed comms feed, idle only. */
    .operator-mascot.mascot-framed[data-pose="idle"]::after {
      animation: mascot-crt-flicker 3.2s steps(2, end) infinite;
    }
    @keyframes mascot-crt-flicker {
      0%, 100% { opacity: 1; }
      48% { opacity: 0.86; }
      52% { opacity: 1; }
    }

    /* ===== KERNEL_PANIC "WOW" reaction (exclusive gold-payload deploy event) ===== */
    /* Frame surges to payload-gold. POSE_COLOR.wow already swings --mascot-status
       to gold (border + text via the var); this adds the bloom + a single launch
       pulse, which eases back to green on revert via the frame's box-shadow transition. */
    .operator-mascot.mascot-framed[data-pose="wow"] {
      box-shadow: 0 0 18px ${KERNEL_PANIC_GOLD}, 0 0 36px rgba(255, 215, 0, 0.55),
        inset 0 0 10px rgba(0, 0, 0, 0.6);
      animation: vera-gold-surge 0.6s ease-out;
    }
    @keyframes vera-gold-surge {
      0%   { box-shadow: 0 0 10px ${KERNEL_PANIC_GOLD}, inset 0 0 8px rgba(0,0,0,0.6); }
      35%  { box-shadow: 0 0 30px #FFF4B0, 0 0 60px rgba(255, 215, 0, 0.8),
               inset 0 0 10px rgba(0, 0, 0, 0.5); }
      100% { box-shadow: 0 0 18px ${KERNEL_PANIC_GOLD}, 0 0 36px rgba(255, 215, 0, 0.55),
               inset 0 0 10px rgba(0, 0, 0, 0.6); }
    }

    /* Gold glitter confetti (burstMascotGlitter) — 4-point sparkles flung outward. */
    .operator-mascot .mascot-glitter {
      position: absolute;
      left: 50%;
      top: 40%;
      width: var(--gsz, 7px);
      height: var(--gsz, 7px);
      z-index: 4;
      pointer-events: none;
      background: radial-gradient(circle, #FFF7CC 0%, ${KERNEL_PANIC_GOLD} 50%,
        rgba(255, 215, 0, 0) 72%);
      clip-path: polygon(50% 0, 58% 42%, 100% 50%, 58% 58%, 50% 100%, 42% 58%, 0 50%, 42% 42%);
      filter: drop-shadow(0 0 4px ${KERNEL_PANIC_GOLD});
      opacity: 0;
      transform: translate(-50%, -50%) scale(0);
      animation: mascot-glitter-pop 0.92s ease-out var(--gd, 0s) forwards;
    }
    @keyframes mascot-glitter-pop {
      0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.2) rotate(0deg); }
      28%  { opacity: 1;
             transform: translate(calc(-50% + var(--gx, 0px) * 0.45),
                                   calc(-50% + var(--gy, 0px) * 0.45))
                        scale(1) rotate(70deg); }
      100% { opacity: 0;
             transform: translate(calc(-50% + var(--gx, 0px)),
                                   calc(-50% + var(--gy, 0px)))
                        scale(0.15) rotate(180deg); }
    }

    /* ===== BL-48 lit comms console (opt-in; the live HUD VERA×FENRIR panel) ===== */
    /* The real engine material lives on the canvas behind the figure; the CSS
       border drops to a faint inner seam so the lit edge isn't doubled. */
    .operator-mascot.mascot-console {
      border-color: color-mix(in srgb, var(--mascot-status, ${COLORS.SENTINEL_GREEN}) 30%, transparent);
      box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.6);
    }
    .operator-mascot.mascot-console .mascot-litframe {
      position: absolute;
      inset: -${CONSOLE_OVERSCAN}px;
      width: calc(100% + ${CONSOLE_OVERSCAN * 2}px);
      height: calc(100% + ${CONSOLE_OVERSCAN * 2}px);
      z-index: 0;
      pointer-events: none;
    }
    /* figure / plate / quip ride above the lit material (scanlines stay at z2). */
    .operator-mascot.mascot-console .mascot-figure { z-index: 1; }
    /* Corner brackets — the "console" read, drawn cheaply on ::before (the
       scanline overlay already owns ::after). Four L-ticks via gradients. */
    .operator-mascot.mascot-console::before {
      content: "";
      position: absolute;
      inset: ${UI_SPACE.hair}px;
      z-index: 3;
      pointer-events: none;
      background:
        linear-gradient(var(--mascot-status), var(--mascot-status)) left top / 9px 1px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) left top / 1px 9px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) right top / 9px 1px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) right top / 1px 9px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) left bottom / 9px 1px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) left bottom / 1px 9px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) right bottom / 9px 1px no-repeat,
        linear-gradient(var(--mascot-status), var(--mascot-status)) right bottom / 1px 9px no-repeat;
      opacity: 0.85;
    }
    /* Settle / value-flash on a pose or quip change (§6.8) — a brief frame surge
       + a tiny scale settle, easing back to rest. Reduced-motion = no pulse. */
    .operator-mascot.mascot-console.mascot-settle {
      animation: mascot-console-settle ${UI_DURATION.settle}ms ${UI_EASE.settle};
    }
    @keyframes mascot-console-settle {
      0% { transform: scale(1.018); box-shadow: inset 0 0 10px rgba(0,0,0,0.6),
             0 0 14px var(--mascot-status, ${COLORS.SENTINEL_GREEN}); }
      100% { transform: scale(1); box-shadow: inset 0 0 10px rgba(0,0,0,0.6); }
    }

    @media (prefers-reduced-motion: reduce) {
      .operator-mascot.mascot-console.mascot-settle { animation: none; }
      .operator-mascot.mascot-framed[data-pose="idle"]::after,
      .operator-mascot.mascot-framed[data-pose="wow"] {
        animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}
